// src/postcards/catalog.js
// Postcard catalog data + pure id/derivation/fact helpers, extracted from
// App.jsx — Phase B Step 2 (Jun 2026). Sibling to src/stamps/catalog.js.
// No imports: pure data + functions that operate only on the catalog below.
// Named exports only (a default is added only if CRA/Webpack complains).

// Convert a destination name to a postcard ID (canonical kebab-case).
// Strips diacritics first so accented characters don't become hyphens.
// Examples:
//   "Lisbon"        → "lisbon"
//   "New York City" → "new-york-city"
//   "St. Petersburg" → "st-petersburg"
//   "Galápagos"     → "galapagos"  (diacritic stripped)
//   "Reykjavík"     → "reykjavik"  (diacritic stripped)
//   "São Paulo"     → "sao-paulo"  (diacritic stripped)
export function destNameToPostcardId(name) {
  if (!name) return null;
  return String(name)
    .normalize("NFD")                  // split accents from base letters
    .replace(/[\u0300-\u036f]/g, "")   // strip combining marks (diacritics)
    .toLowerCase()
    .replace(/[.']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Compute the set of postcard IDs the user has "discovered" via derivation
// from existing state (boards, wishlist, past trips). Returns a Map keyed
// by postcardId with value = inferred state ("dreaming" or "visited").
//
// Derivation rules (Phase 1):
//   - Destination in any board   → dreaming
//   - Destination in wishlist    → dreaming
//   - Destination in past trips  → visited (completed = actually went)
//
// Phase 2 will add: swipe-right destinations, Surprise Me results,
// TMA matches, Mood Match shortlist — all → dreaming.
export function computePostcardDerivation({ boards = [], wishDestNames = [], pastTripDestNames = [] }) {
  const map = new Map();
  // Dreaming sources first
  (boards || []).forEach(b => {
    (b.items || []).forEach(item => {
      const id = destNameToPostcardId(item.name || item.destination || item);
      if (id) map.set(id, "dreaming");
    });
  });
  (wishDestNames || []).forEach(name => {
    const id = destNameToPostcardId(name);
    if (id) map.set(id, "dreaming");
  });
  // Visited sources override (a destination you actually visited shouldn't
  // still show as dreaming, even if it's also in your wishlist).
  (pastTripDestNames || []).forEach(name => {
    const id = destNameToPostcardId(name);
    if (id) map.set(id, "visited");
  });
  return map;
}

// ── POSTCARDS_CATALOG ─────────────────────────────────────────────
// Master list of 100 destination postcards. Each entry:
//   {
//     id: "lisbon",           // kebab-case, matches destNameToPostcardId()
//     destName: "Lisbon",     // matches a DESTINATIONS[].name
//     country: "PORTUGAL",    // ALL CAPS for editorial eyebrow display
//     title: "Lisbon",        // display name for the postcard
//     vibe: "Laid-back, soulful, wine-soaked",  // short hero blurb
//     priceTier: "moderate",  // "modest" | "moderate" | "luxe" — informs
//                             //   budget filtering and detail modal copy
//     region: "Europe",       // matches a DESTINATIONS[].region
//   }
//
// Postcard chat owns this catalog. They generate 100 entries from the
// finished postcard set. The empty array below is a placeholder — App.jsx
// surfaces gracefully handle an empty catalog by showing the empty state.
export const POSTCARDS_CATALOG = [
  {
    id: "paris",
    destName: "Paris",
    country: "FRANCE",
    title: "Paris",
    vibe: "Boulevards, light & golden-hour cafés",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Eiffel Tower · Louvre · Montmartre",
    mustSee: "Musée d'Orsay",
    mustEat: "Croissants at Du Pain et des Idées",
    mustDo: "Sunset on Pont des Arts",
    bestTime: "April-June or September-October",
    currency: "Euro (EUR)",
    language: "French",
    funFact: "There are more dogs than children in Paris.",
  },
  {
    id: "tokyo",
    destName: "Tokyo",
    country: "JAPAN",
    title: "Tokyo",
    vibe: "Neon energy, ramen & vending-machine streets",
    priceTier: "moderate",
    region: "East Asia",
    landmarks: "Shibuya Crossing · Senso-ji · Tsukiji Market",
    mustSee: "Meiji Shrine forest walk",
    mustEat: "Omakase sushi at Tsukiji",
    mustDo: "Karaoke in Shibuya",
    bestTime: "March-May (cherry blossoms) or October-November",
    currency: "Japanese Yen (JPY)",
    language: "Japanese",
    funFact: "Shibuya Crossing sees up to 3,000 people cross at once during peak.",
  },
  {
    id: "new-york-city",
    destName: "New York City",
    country: "USA",
    title: "New York City",
    vibe: "Skyline, slice & yellow-cab pulse",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Statue of Liberty · Central Park · Times Square",
    mustSee: "The High Line",
    mustEat: "Bagels at Russ & Daughters",
    mustDo: "Catch a Broadway show",
    bestTime: "April-June or September-November",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "The Empire State Building has its own zip code: 10118.",
  },
  {
    id: "barcelona",
    destName: "Barcelona",
    country: "SPAIN",
    title: "Barcelona",
    vibe: "Gaudí, tapas & Mediterranean swagger",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Sagrada Família · La Rambla · Park Güell",
    mustSee: "Casa Batlló interior",
    mustEat: "Late-night tapas in El Born",
    mustDo: "Sunrise at Bunkers del Carmel",
    bestTime: "May-June or September-October",
    currency: "Euro (EUR)",
    language: "Spanish, Catalan",
    funFact: "La Sagrada Família has been under construction since 1882.",
  },
  {
    id: "reykjavik",
    destName: "Reykjavik",
    country: "ICELAND",
    title: "Reykjavik",
    vibe: "Aurora, geothermal & North Atlantic edge",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Blue Lagoon · Hallgrímskirkja · Northern Lights",
    mustSee: "Northern Lights chase",
    mustEat: "Lobster soup at Sægreifinn",
    mustDo: "Soak in the Sky Lagoon",
    bestTime: "June-August for sun, September-March for Northern Lights",
    currency: "Icelandic Króna (ISK)",
    language: "Icelandic",
    funFact: "Iceland has no McDonald's — the last one closed in 2009.",
  },
  {
    id: "lisbon",
    destName: "Lisbon",
    country: "PORTUGAL",
    title: "Lisbon",
    vibe: "Laid-back, soulful & wine-soaked",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Tram 28 · Belém Tower · Alfama district",
    mustSee: "Jerónimos Monastery",
    mustEat: "Pastéis de Nata at Belém",
    mustDo: "Ride Tram 28 through Alfama",
    bestTime: "March-May or September-October",
    currency: "Euro (EUR)",
    language: "Portuguese",
    funFact: "Lisbon is older than Rome — settled over 3,000 years ago.",
  },
  {
    id: "porto",
    destName: "Porto",
    country: "PORTUGAL",
    title: "Porto",
    vibe: "Riverside Port wine & old-world charm",
    priceTier: "modest",
    region: "Europe",
    landmarks: "Ribeira · Dom Luís I Bridge · port wine cellars",
    mustSee: "Livraria Lello bookstore",
    mustEat: "Francesinha at Café Santiago",
    mustDo: "Port wine tasting in Vila Nova de Gaia",
    bestTime: "May-September",
    currency: "Euro (EUR)",
    language: "Portuguese",
    funFact: "Real port wine can only come from this region — it's protected by law.",
  },
  {
    id: "rome",
    destName: "Rome",
    country: "ITALY",
    title: "Rome",
    vibe: "Ruins, pasta & golden-hour aperitivo",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Colosseum · Vatican · Trevi Fountain",
    mustSee: "Pantheon at dawn",
    mustEat: "Cacio e pepe in Trastevere",
    mustDo: "Throw a coin in Trevi Fountain",
    bestTime: "April-May or September-October",
    currency: "Euro (EUR)",
    language: "Italian",
    funFact: "Romans throw an average of €1.5 million into the Trevi Fountain each year.",
  },
  {
    id: "santorini",
    destName: "Santorini",
    country: "GREECE",
    title: "Santorini",
    vibe: "Whitewashed cliffs above the Aegean",
    priceTier: "luxe",
    region: "Europe",
    landmarks: "Oia sunsets · caldera views · Red Beach",
    mustSee: "Oia sunset",
    mustEat: "Fresh seafood in Ammoudi Bay",
    mustDo: "Sail the volcanic caldera",
    bestTime: "May-June or September (skip August crowds)",
    currency: "Euro (EUR)",
    language: "Greek",
    funFact: "Santorini's caldera is the remnant of one of history's largest volcanic eruptions.",
  },
  {
    id: "athens",
    destName: "Athens",
    country: "GREECE",
    title: "Athens",
    vibe: "Ancient stones & Mediterranean warmth",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Acropolis · Parthenon · Plaka district",
    mustSee: "Acropolis at golden hour",
    mustEat: "Souvlaki in Plaka",
    mustDo: "Day trip to Cape Sounion",
    bestTime: "April-May or October",
    currency: "Euro (EUR)",
    language: "Greek",
    funFact: "Athens is one of the world's oldest continuously inhabited cities — over 3,400 years.",
  },
  {
    id: "prague",
    destName: "Prague",
    country: "CZECHIA",
    title: "Prague",
    vibe: "Gothic spires & cobblestone fairytale",
    priceTier: "modest",
    region: "Europe",
    landmarks: "Old Town Square · Charles Bridge · Prague Castle",
    mustSee: "Astronomical Clock at the hour",
    mustEat: "Svíčková at Lokál",
    mustDo: "Vltava river boat cruise",
    bestTime: "April-May or September-October",
    currency: "Czech Koruna (CZK)",
    language: "Czech",
    funFact: "Prague has more castles per square mile than anywhere else in Europe.",
  },
  {
    id: "budapest",
    destName: "Budapest",
    country: "HUNGARY",
    title: "Budapest",
    vibe: "Thermal baths & Danube grandeur",
    priceTier: "modest",
    region: "Europe",
    landmarks: "Parliament · Széchenyi Baths · Chain Bridge",
    mustSee: "Buda Castle district",
    mustEat: "Goulash at Bestia",
    mustDo: "Soak in Széchenyi Thermal Baths",
    bestTime: "April-June or September-October",
    currency: "Hungarian Forint (HUF)",
    language: "Hungarian",
    funFact: "Budapest has 125 thermal springs feeding its famous baths.",
  },
  {
    id: "copenhagen",
    destName: "Copenhagen",
    country: "DENMARK",
    title: "Copenhagen",
    vibe: "Hygge canals & design-forward cool",
    priceTier: "moderate",
    region: "Europe",
    landmarks: "Nyhavn · Tivoli Gardens · Little Mermaid",
    mustSee: "Nyhavn harbor walk",
    mustEat: "Smørrebrød at Aamanns",
    mustDo: "Rent a bike, explore Christianshavn",
    bestTime: "June-August",
    currency: "Danish Krone (DKK)",
    language: "Danish",
    funFact: "More than half of Copenhageners bike to work every day, even in winter.",
  },
  {
    id: "bangkok",
    destName: "Bangkok",
    country: "THAILAND",
    title: "Bangkok",
    vibe: "Neon temples & street-food chaos",
    priceTier: "modest",
    region: "Southeast Asia",
    landmarks: "Grand Palace · Wat Arun · Chatuchak Market",
    mustSee: "Wat Pho reclining Buddha",
    mustEat: "Pad Thai at Thip Samai",
    mustDo: "Long-tail boat through the klongs",
    bestTime: "November-February (dry, cooler season)",
    currency: "Thai Baht (THB)",
    language: "Thai",
    funFact: "Bangkok's official ceremonial name has 168 letters — the longest city name on earth.",
  },
  {
    id: "bali",
    destName: "Bali",
    country: "INDONESIA",
    title: "Bali",
    vibe: "Temples, rice terraces & jungle soul",
    priceTier: "modest",
    region: "Southeast Asia",
    landmarks: "Ubud rice terraces · Tanah Lot · Uluwatu cliffs",
    mustSee: "Tegallalang rice terraces",
    mustEat: "Babi guling in Ubud",
    mustDo: "Sunrise hike up Mount Batur",
    bestTime: "April-October (dry season)",
    currency: "Indonesian Rupiah (IDR)",
    language: "Indonesian, Balinese",
    funFact: "Balinese Hindus believe their island is the world's spiritual center.",
  },
  {
    id: "hoi-an",
    destName: "Hoi An",
    country: "VIETNAM",
    title: "Hoi An",
    vibe: "Lantern-lit riverside ancient town",
    priceTier: "modest",
    region: "Southeast Asia",
    landmarks: "Lantern-lit Old Town · Japanese Bridge · An Bang Beach",
    mustSee: "Old Town lantern-lit at dusk",
    mustEat: "Cao lầu noodles at Madam Khanh's",
    mustDo: "Get a custom suit tailored",
    bestTime: "February-April",
    currency: "Vietnamese Dong (VND)",
    language: "Vietnamese",
    funFact: "Hoi An's Old Town turns off electric lights once a month for the full-moon lantern festival.",
  },
  {
    id: "kyoto",
    destName: "Kyoto",
    country: "JAPAN",
    title: "Kyoto",
    vibe: "Zen gardens, temples & quiet ceremony",
    priceTier: "moderate",
    region: "East Asia",
    landmarks: "Fushimi Inari · Arashiyama bamboo · Kinkaku-ji",
    mustSee: "Fushimi Inari torii gates",
    mustEat: "Kaiseki dinner in Gion",
    mustDo: "Tea ceremony at Camellia",
    bestTime: "March-May or October-November (for fall colors)",
    currency: "Japanese Yen (JPY)",
    language: "Japanese",
    funFact: "Kyoto has over 1,600 Buddhist temples — more than anywhere else in Japan.",
  },
  {
    id: "seoul",
    destName: "Seoul",
    country: "SOUTH KOREA",
    title: "Seoul",
    vibe: "Hyper-modern energy meets palace calm",
    priceTier: "moderate",
    region: "East Asia",
    landmarks: "Gyeongbokgung · Bukchon Hanok · Myeongdong",
    mustSee: "Bukchon Hanok Village",
    mustEat: "Korean BBQ in Mapo",
    mustDo: "Late-night street food in Myeongdong",
    bestTime: "April-June or September-October",
    currency: "South Korean Won (KRW)",
    language: "Korean",
    funFact: "Seoul has more coffee shops per capita than any other city in the world.",
  },
  {
    id: "miami",
    destName: "Miami",
    country: "USA",
    title: "Miami",
    vibe: "Art Deco neon & oceanfront pulse",
    priceTier: "moderate",
    region: "North America",
    landmarks: "South Beach · Wynwood Walls · Little Havana",
    mustSee: "Wynwood Walls murals",
    mustEat: "Cuban sandwich at Versailles",
    mustDo: "Sunset cruise on Biscayne Bay",
    bestTime: "December-April (dry season)",
    currency: "US Dollar (USD)",
    language: "English, Spanish",
    funFact: "Miami is the only major US city founded by a woman — Julia Tuttle, in 1896.",
  },
  {
    id: "new-orleans",
    destName: "New Orleans",
    country: "USA",
    title: "New Orleans",
    vibe: "Jazz, gumbo & wrought-iron mystery",
    priceTier: "moderate",
    region: "North America",
    landmarks: "French Quarter · Bourbon Street · Jackson Square",
    mustSee: "St. Louis Cathedral",
    mustEat: "Beignets at Café du Monde",
    mustDo: "Live jazz on Frenchmen Street",
    bestTime: "February-May (or October for milder weather)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "Bourbon Street is named after the French royal family, not the whiskey.",
  },
  {
    id: "san-francisco",
    destName: "San Francisco",
    country: "USA",
    title: "San Francisco",
    vibe: "Foggy hills, bridges & quirky charm",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Golden Gate · Alcatraz · Painted Ladies",
    mustSee: "Walk the Golden Gate Bridge",
    mustEat: "Sourdough at Tartine Bakery",
    mustDo: "Ride a cable car to Lombard",
    bestTime: "September-November (warmest, least foggy)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "The Golden Gate Bridge is painted continuously — they start over as soon as they finish.",
  },
  {
    id: "honolulu",
    destName: "Honolulu",
    country: "USA",
    title: "Honolulu",
    vibe: "Volcanic shores & aloha rhythm",
    priceTier: "luxe",
    region: "North America",
    landmarks: "Waikiki Beach · Diamond Head · Pearl Harbor",
    mustSee: "Diamond Head crater hike",
    mustEat: "Poke bowl at Ono Seafood",
    mustDo: "Surf lesson at Waikiki Beach",
    bestTime: "April-June or September-November",
    currency: "US Dollar (USD)",
    language: "English, Hawaiian",
    funFact: "Hawaiian is one of only two official state languages in the entire US.",
  },
  {
    id: "los-angeles",
    destName: "Los Angeles",
    country: "USA",
    title: "Los Angeles",
    vibe: "Palms, beaches & cinematic light",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Hollywood Sign · Venice Beach · Griffith Observatory",
    mustSee: "Griffith Observatory at sunset",
    mustEat: "Tacos at Guerrilla Tacos",
    mustDo: "Drive Mulholland to Malibu",
    bestTime: "March-May or September-November",
    currency: "US Dollar (USD)",
    language: "English, Spanish",
    funFact: "LA's Hollywood sign originally read 'HOLLYWOODLAND' — it lost the last 4 letters in 1949.",
  },
  {
    id: "las-vegas",
    destName: "Las Vegas",
    country: "USA",
    title: "Las Vegas",
    vibe: "Desert mirage of neon excess",
    priceTier: "moderate",
    region: "North America",
    landmarks: "The Strip · Bellagio fountains · Fremont Street",
    mustSee: "Bellagio fountains show",
    mustEat: "Dinner at Joël Robuchon",
    mustDo: "Helicopter ride over the Strip",
    bestTime: "March-May or September-November (avoid summer)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "There are no clocks or windows in most Vegas casino floors — by design.",
  },
  {
    id: "chicago",
    destName: "Chicago",
    country: "USA",
    title: "Chicago",
    vibe: "Lakefront skyline & deep-dish soul",
    priceTier: "moderate",
    region: "North America",
    landmarks: "The Bean · Navy Pier · Willis Tower",
    mustSee: "The Bean in Millennium Park",
    mustEat: "Deep dish at Lou Malnati's",
    mustDo: "Architecture river cruise",
    bestTime: "June-September (winters are brutal)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "Chicago invented the skyscraper — the world's first was the 10-story Home Insurance Building.",
  },
  {
    id: "nashville",
    destName: "Nashville",
    country: "USA",
    title: "Nashville",
    vibe: "Honky-tonks & Music City heart",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Broadway honky-tonks · Grand Ole Opry · Music Row",
    mustSee: "Country Music Hall of Fame",
    mustEat: "Hot chicken at Hattie B's",
    mustDo: "Honky-tonk hop on Broadway",
    bestTime: "April-May or September-October",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "Nashville is nicknamed 'Music City' — there are more songwriters per capita than anywhere else.",
  },
  {
    id: "washington-dc",
    destName: "Washington DC",
    country: "USA",
    title: "Washington DC",
    vibe: "Monuments, museums & marble gravitas",
    priceTier: "moderate",
    region: "North America",
    landmarks: "National Mall · Lincoln Memorial · Smithsonian",
    mustSee: "Lincoln Memorial at night",
    mustEat: "Half-smoke at Ben's Chili Bowl",
    mustDo: "Smithsonian museum crawl",
    bestTime: "March-May (cherry blossoms) or September-October",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "DC is the only US capital where residents had no congressional voting rights until 1964.",
  },
  {
    id: "boston",
    destName: "Boston",
    country: "USA",
    title: "Boston",
    vibe: "Colonial cobblestones & harbor history",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Freedom Trail · Fenway Park · Harvard Yard",
    mustSee: "Walk the Freedom Trail",
    mustEat: "Lobster roll at Neptune Oyster",
    mustDo: "Catch a game at Fenway",
    bestTime: "June-October (skip the harsh winter)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "Boston's subway system, the T, is the oldest in the US (opened 1897).",
  },
  {
    id: "seattle",
    destName: "Seattle",
    country: "USA",
    title: "Seattle",
    vibe: "Mountain mist, coffee & evergreen calm",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Space Needle · Pike Place · Mt Rainier views",
    mustSee: "Pike Place fish market",
    mustEat: "Coffee at the original Starbucks",
    mustDo: "Ferry ride to Bainbridge Island",
    bestTime: "July-September (the dry summer surprise)",
    currency: "US Dollar (USD)",
    language: "English",
    funFact: "Seattle has more glassblowers per capita than anywhere else in the US.",
  },
  {
    id: "mexico-city",
    destName: "Mexico City",
    country: "MEXICO",
    title: "Mexico City",
    vibe: "Pyramids, mezcal & artistic exuberance",
    priceTier: "modest",
    region: "North America",
    landmarks: "Zócalo · Frida Kahlo Museum · Teotihuacán",
    mustSee: "Frida Kahlo's Casa Azul",
    mustEat: "Tacos al pastor at El Vilsito",
    mustDo: "Lucha libre at Arena México",
    bestTime: "March-May (warm, dry) or November (mild)",
    currency: "Mexican Peso (MXN)",
    language: "Spanish",
    funFact: "Mexico City sits on a lake bed and is sinking about 20 inches per year.",
  },
  {
    id: "tulum",
    destName: "Tulum",
    country: "MEXICO",
    title: "Tulum",
    vibe: "Cenotes, cliffs & turquoise jungle calm",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Mayan ruins · cenotes · beach club scene",
    mustSee: "Tulum Mayan ruins at sunrise",
    mustEat: "Fresh ceviche at Hartwood",
    mustDo: "Swim in a cenote",
    bestTime: "November-April (dry season)",
    currency: "Mexican Peso (MXN)",
    language: "Spanish",
    funFact: "Tulum is the only Mayan city built right on the Caribbean coast.",
  },
  {
    id: "buenos-aires",
    destName: "Buenos Aires",
    country: "ARGENTINA",
    title: "Buenos Aires",
    vibe: "Tango nights & Belle Époque grandeur",
    priceTier: "modest",
    region: "South America",
    landmarks: "La Boca · Recoleta · tango halls",
    mustSee: "Recoleta Cemetery",
    mustEat: "Steak at Don Julio",
    mustDo: "Tango show in San Telmo",
    bestTime: "September-November or March-May",
    currency: "Argentine Peso (ARS)",
    language: "Spanish",
    funFact: "Buenos Aires has the world's widest avenue: 9 de Julio, 16 lanes across.",
  },
  {
    id: "patagonia",
    destName: "Patagonia",
    country: "ARGENTINA",
    title: "Patagonia",
    vibe: "Granite spires & glacial wilderness",
    priceTier: "moderate",
    region: "South America",
    landmarks: "Torres del Paine · Perito Moreno · Fitz Roy",
    mustSee: "Torres del Paine W trek",
    mustEat: "Asado lamb in Puerto Natales",
    mustDo: "Glacier kayak at Perito Moreno",
    bestTime: "November-March (Southern Hemisphere summer)",
    currency: "Argentine Peso / Chilean Peso",
    language: "Spanish",
    funFact: "Patagonia spans two countries and is larger than Texas, with fewer than 2 million people.",
  },
  {
    id: "cartagena",
    destName: "Cartagena",
    country: "COLOMBIA",
    title: "Cartagena",
    vibe: "Walled colonial color on the Caribbean",
    priceTier: "moderate",
    region: "South America",
    landmarks: "walled city · Bocagrande · Rosario Islands",
    mustSee: "Walled Old City at dusk",
    mustEat: "Ceviche at La Cevichería",
    mustDo: "Day sail to Rosario Islands",
    bestTime: "December-April (dry season)",
    currency: "Colombian Peso (COP)",
    language: "Spanish",
    funFact: "Cartagena's walled city has stood since 1586 — the most intact colonial fortifications in the Americas.",
  },
  {
    id: "marrakech",
    destName: "Marrakech",
    country: "MOROCCO",
    title: "Marrakech",
    vibe: "Souks, riads & desert spice",
    priceTier: "modest",
    region: "Middle East & North Africa",
    landmarks: "Jemaa el-Fnaa · Majorelle Garden · the medina",
    mustSee: "Jemaa el-Fnaa at night",
    mustEat: "Tagine at Nomad",
    mustDo: "Get lost in the souks",
    bestTime: "March-May or September-November",
    currency: "Moroccan Dirham (MAD)",
    language: "Arabic, Berber, French",
    funFact: "Marrakech's medina has over 9,000 alleys — getting lost is part of the experience.",
  },
  {
    id: "cairo",
    destName: "Cairo",
    country: "EGYPT",
    title: "Cairo",
    vibe: "Pyramids, Nile & ancient gravity",
    priceTier: "modest",
    region: "Middle East & North Africa",
    landmarks: "Pyramids of Giza · Egyptian Museum · Khan el-Khalili",
    mustSee: "Pyramids of Giza & Sphinx",
    mustEat: "Koshary at Abou Tarek",
    mustDo: "Sunset felucca on the Nile",
    bestTime: "October-April (skip the summer heat)",
    currency: "Egyptian Pound (EGP)",
    language: "Arabic",
    funFact: "The Great Pyramid was the tallest human-made structure for nearly 4,000 years.",
  },
  {
    id: "cape-town",
    destName: "Cape Town",
    country: "SOUTH AFRICA",
    title: "Cape Town",
    vibe: "Mountain meets ocean, wild & refined",
    priceTier: "moderate",
    region: "Sub-Saharan Africa",
    landmarks: "Table Mountain · V&A Waterfront · Cape Peninsula",
    mustSee: "Table Mountain cable car",
    mustEat: "Bunny chow at Mariam's Kitchen",
    mustDo: "Cape Peninsula coastal drive",
    bestTime: "November-March (Southern summer)",
    currency: "South African Rand (ZAR)",
    language: "English, Afrikaans, Xhosa",
    funFact: "Table Mountain is one of the world's oldest mountains — about 6 times older than the Himalayas.",
  },
  {
    id: "nairobi",
    destName: "Nairobi",
    country: "KENYA",
    title: "Nairobi",
    vibe: "Safari gateway & savanna heartbeat",
    priceTier: "moderate",
    region: "Sub-Saharan Africa",
    landmarks: "Nairobi National Park · Giraffe Centre · Karen Blixen",
    mustSee: "Giraffe Centre breakfast",
    mustEat: "Nyama choma at Carnivore",
    mustDo: "Safari day at Nairobi National Park",
    bestTime: "June-October or January-February (dry seasons)",
    currency: "Kenyan Shilling (KES)",
    language: "Swahili, English",
    funFact: "Nairobi is the only capital with a national park inside city limits.",
  },
  {
    id: "dubai",
    destName: "Dubai",
    country: "UAE",
    title: "Dubai",
    vibe: "Desert megacity of glass & gold",
    priceTier: "luxe",
    region: "Middle East & North Africa",
    landmarks: "Burj Khalifa · Palm Jumeirah · Desert Safari",
    mustSee: "Burj Khalifa observation deck",
    mustEat: "Shawarma at Al Mallah",
    mustDo: "Sunset desert safari",
    bestTime: "November-March (cooler season)",
    currency: "UAE Dirham (AED)",
    language: "Arabic, English",
    funFact: "About 85% of Dubai's residents are foreign-born expats.",
  },
  {
    id: "sydney",
    destName: "Sydney",
    country: "AUSTRALIA",
    title: "Sydney",
    vibe: "Harbor icons & sun-baked beaches",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Opera House · Harbour Bridge · Bondi Beach",
    mustSee: "Sydney Opera House tour",
    mustEat: "Fish & chips at Doyles",
    mustDo: "Coastal walk Bondi to Coogee",
    bestTime: "September-November or March-May",
    currency: "Australian Dollar (AUD)",
    language: "English",
    funFact: "The Sydney Opera House has over 1 million roof tiles — and was finished 14 years late.",
  },
  {
    id: "melbourne",
    destName: "Melbourne",
    country: "AUSTRALIA",
    title: "Melbourne",
    vibe: "Laneway cafés & arts-driven cool",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Federation Square · laneway cafés · Great Ocean Road",
    mustSee: "National Gallery of Victoria",
    mustEat: "Brunch in a Fitzroy laneway",
    mustDo: "Great Ocean Road day trip",
    bestTime: "March-May (autumn) or September-November (spring)",
    currency: "Australian Dollar (AUD)",
    language: "English",
    funFact: "Melbourne is consistently ranked one of the world's most liveable cities.",
  },
  {
    id: "auckland",
    destName: "Auckland",
    country: "NEW ZEALAND",
    title: "Auckland",
    vibe: "City of sails between two harbors",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Sky Tower · Waiheke wineries · Mt Eden",
    mustSee: "Sky Tower SkyWalk",
    mustEat: "Fresh oysters at Depot Eatery",
    mustDo: "Ferry + wine on Waiheke Island",
    bestTime: "November-April (Southern summer)",
    currency: "New Zealand Dollar (NZD)",
    language: "English, Māori",
    funFact: "Auckland is built on 53 dormant volcanoes.",
  },
  {
    id: "queenstown",
    destName: "Queenstown",
    country: "NEW ZEALAND",
    title: "Queenstown",
    vibe: "Alpine adventure capital of the South",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Lake Wakatipu · Skyline Gondola · Milford Sound",
    mustSee: "Lake Wakatipu viewpoint",
    mustEat: "Fergburger (the original)",
    mustDo: "Bungee jump at Kawarau Bridge",
    bestTime: "December-March for adventure, June-August for skiing",
    currency: "New Zealand Dollar (NZD)",
    language: "English",
    funFact: "Queenstown is the birthplace of commercial bungee jumping — first jump in 1988.",
  },
  {
    id: "vancouver",
    destName: "Vancouver",
    country: "CANADA",
    title: "Vancouver",
    vibe: "Glass towers, peaks & Pacific cool",
    priceTier: "moderate",
    region: "North America",
    landmarks: "Stanley Park · Canada Place · Capilano Suspension Bridge",
    mustSee: "Stanley Park seawall & totem poles",
    mustEat: "Fresh sushi or a Japadog on Robson Street",
    mustDo: "Bike the seawall, then ride the gondola up Grouse Mountain",
    bestTime: "June-September for sun, December-March for nearby skiing",
    currency: "Canadian Dollar (CAD)",
    language: "English",
    funFact: "Vancouver is one of the few cities where you can ski and hit the beach in the same day.",
  },
  {
    id: "abu-dhabi",
    destName: "Abu Dhabi",
    country: "UAE",
    title: "Abu Dhabi",
    vibe: "Marble domes, falcons & desert gold",
    priceTier: "luxe",
    region: "Middle East & North Africa",
    landmarks: "Sheikh Zayed Grand Mosque · Louvre Abu Dhabi · Qasr Al Watan",
    mustSee: "Sheikh Zayed Grand Mosque at sunset",
    mustEat: "Fresh dates and Emirati machboos",
    mustDo: "Dune drive in the Liwa Desert, then Arabic coffee in a majlis",
    bestTime: "November-March for cooler desert days",
    currency: "UAE Dirham (AED)",
    language: "Arabic",
    funFact: "The Sheikh Zayed Grand Mosque holds the world's largest hand-knotted carpet.",
  },
  {
    id: "maldives",
    destName: "Maldives",
    country: "MALDIVES",
    title: "Maldives",
    vibe: "Overwater villas, reefs & turquoise hush",
    priceTier: "luxe",
    region: "Indian Ocean",
    landmarks: "Overwater bungalows · Coral atolls · Malé",
    mustSee: "A bioluminescent beach glowing at night",
    mustEat: "Mas huni with roshi for a local breakfast",
    mustDo: "Snorkel or dive the reefs with manta rays and turtles",
    bestTime: "November-April for dry, sunny days",
    currency: "Maldivian Rufiyaa (MVR)",
    language: "Dhivehi",
    funFact: "The Maldives is the world's lowest-lying country — averaging just 1.5m above sea level.",
  },
  {
    id: "amalfi-coast",
    destName: "Amalfi Coast",
    country: "ITALY",
    title: "Amalfi Coast",
    vibe: "Cliffside pastels, lemons & the Tyrrhenian blue",
    priceTier: "luxe",
    region: "Europe",
    landmarks: "Positano · Ravello · Path of the Gods",
    mustSee: "Positano's tumbling pastel houses from the water",
    mustEat: "Fresh seafood and a glass of limoncello",
    mustDo: "Drive the coastal road, then a boat trip to Capri",
    bestTime: "May-June or September for warm seas without the crush",
    currency: "Euro (EUR)",
    language: "Italian",
    funFact: "The region's lemons are so prized they have protected status as 'Sfusato Amalfitano.'",
  },
  {
    id: "chiang-mai",
    destName: "Chiang Mai",
    country: "THAILAND",
    title: "Chiang Mai",
    vibe: "Lantern light, temples & northern calm",
    priceTier: "budget",
    region: "Asia",
    landmarks: "Doi Suthep · Old City temples · Night Bazaar",
    mustSee: "Wat Phra That Doi Suthep above the city",
    mustEat: "Khao soi — northern Thai curry noodles",
    mustDo: "Release a lantern during the Yi Peng festival",
    bestTime: "November-February for cool, dry weather",
    currency: "Thai Baht (THB)",
    language: "Thai",
    funFact: "Chiang Mai has over 300 temples — more per square mile than almost anywhere.",
  },
  {
    id: "lombok",
    destName: "Lombok",
    country: "INDONESIA",
    title: "Lombok",
    vibe: "Volcano peaks, surf breaks & quiet shores",
    priceTier: "budget",
    region: "Asia",
    landmarks: "Mount Rinjani · Gili Islands · Kuta Lombok",
    mustSee: "Sunrise over Mount Rinjani's crater lake",
    mustEat: "Ayam taliwang — fiery Lombok grilled chicken",
    mustDo: "Island-hop the Gilis, then surf the south coast",
    bestTime: "May-September for dry season",
    currency: "Indonesian Rupiah (IDR)",
    language: "Indonesian & Sasak",
    funFact: "Lombok's name is thought to come from a word meaning 'straight' — not the chili pepper.",
  },
  {
    id: "amman",
    destName: "Amman",
    country: "JORDAN",
    title: "Amman",
    vibe: "Limestone hills, ruins & mansaf warmth",
    priceTier: "modest",
    region: "Middle East & North Africa",
    landmarks: "Amman Citadel · Roman Theatre · Rainbow Street",
    mustSee: "The Temple of Hercules columns at the Citadel",
    mustEat: "Mansaf — lamb, rice and jameed, the national dish",
    mustDo: "Float in the Dead Sea, an hour's drive west",
    bestTime: "March-May or September-November for mild weather",
    currency: "Jordanian Dinar (JOD)",
    language: "Arabic",
    funFact: "Amman was built on seven hills, like Rome — and was once called Philadelphia.",
  },
  {
    id: "cairns",
    destName: "Cairns",
    country: "AUSTRALIA",
    title: "Cairns",
    vibe: "Reef, rainforest & tropical north edge",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Great Barrier Reef · Daintree Rainforest · Cairns Esplanade",
    mustSee: "The Great Barrier Reef on a day dive trip",
    mustEat: "Fresh barramundi and tropical mango",
    mustDo: "Snorkel the reef, then explore the Daintree by day",
    bestTime: "May-October for dry, sunny days",
    currency: "Australian Dollar (AUD)",
    language: "English",
    funFact: "Cairns sits between two World Heritage sites — the reef and the rainforest.",
  },
  {
    id: "perth",
    destName: "Perth",
    country: "AUSTRALIA",
    title: "Perth",
    vibe: "Swan River, sunsets & quokka cheer",
    priceTier: "moderate",
    region: "Oceania",
    landmarks: "Kings Park · Swan River · Rottnest Island",
    mustSee: "Sunset over the Indian Ocean at Cottesloe Beach",
    mustEat: "Fresh WA rock lobster and Swan Valley wine",
    mustDo: "Ferry to Rottnest Island for a quokka selfie",
    bestTime: "September-November or March-May for warm, dry days",
    currency: "Australian Dollar (AUD)",
    language: "English",
    funFact: "Perth is one of the most isolated major cities on Earth — nearest big city is 2,000km away.",
  },
  {
    id: "hobart",
    destName: "Hobart",
    country: "AUSTRALIA",
    title: "Hobart",
    vibe: "Sandstone wharves, mountain & whisky air",
    priceTier: "moderate",
    region: "Oceania",
    funFact: "Hobart is the second-oldest capital city in Australia and sits closer to Antarctica than to Cairns.",
  },
  {
    id: "komodo",
    destName: "Komodo",
    country: "INDONESIA",
    title: "Komodo",
    vibe: "Dragons, pink sand & dry savanna edge",
    priceTier: "moderate",
    region: "Southeast Asia",
    funFact: "Komodo dragons can detect prey up to 9 kilometers away using their forked tongues — they were the inspiration for some early 'dragon' myths.",
  },
  {
    id: "raja-ampat",
    destName: "Raja Ampat",
    country: "INDONESIA",
    title: "Raja Ampat",
    vibe: "Karst islands, reefs & marine paradise",
    priceTier: "luxe",
    region: "Southeast Asia",
    funFact: "Raja Ampat has more marine biodiversity than anywhere else on Earth — over 1,500 fish species and 75% of all known coral species live here.",
  },
  {
    id: "napa-valley",
    destName: "Napa Valley",
    country: "USA",
    title: "Napa Valley",
    vibe: "Vineyards, oak hills & cellar warmth",
    priceTier: "luxe",
    region: "North America",
    funFact: "Napa Valley is only 30 miles long but contains 16 distinct soil types — more geological variety than most of France.",
  },
  {
    id: "rotorua",
    destName: "Rotorua",
    country: "NEW ZEALAND",
    title: "Rotorua",
    vibe: "Geysers, geothermal steam & Māori soul",
    priceTier: "moderate",
    region: "Oceania",
    funFact: "Rotorua sits on the Pacific Ring of Fire — locals call the constant sulfur smell 'the perfume of the gods.'",
  },
  {
    id: "yogyakarta",
    destName: "Yogyakarta",
    country: "INDONESIA",
    title: "Yogyakarta",
    vibe: "Temples, batik & Javanese refinement",
    priceTier: "modest",
    region: "Southeast Asia",
    funFact: "Yogyakarta's Borobudur is the world's largest Buddhist temple, with over 2,600 carved relief panels and 504 Buddha statues.",
  },
];

// ── Fallback fun-facts for destinations not yet in POSTCARDS_CATALOG ──
// These 18 destinations exist in DESTINATIONS but don't yet have full
// postcard illustrations / catalog entries (most are pending postcard work).
// This map lets the post-save fact card still surface delight for them.
// When a destination gets a full POSTCARDS_CATALOG entry, that entry's
// funFact takes precedence (see getFunFactForDest helper below).
export const MISSING_POSTCARD_FACTS = {
  "Atlanta": "Atlanta is home to the world's busiest airport — more passengers move through it annually than the population of the entire UK.",
  "Austin": "Every summer evening, 1.5 million Mexican free-tailed bats stream out from under the Congress Avenue Bridge — the largest urban bat colony in North America.",
  "Charleston": "Charleston's cobblestone streets were paved with stones brought as ballast on European trading ships — they were too valuable to throw away.",
  "Dallas": "The frozen margarita was invented in Dallas in 1971, and the original machine is in the Smithsonian.",
  "Denver": "Denver gets more annual sunshine than San Diego or Miami Beach — over 300 sunny days a year despite the mountain reputation.",
  "Houston": "Houston has no zoning laws — strip malls, mansions, and refineries can legally sit on the same block.",
  "New York — The Hamptons": "The Hamptons potato farms outnumbered estates until the 1970s — that's why you still see windmills along the highway.",
  "Phoenix": "Phoenix is named for the mythological bird because the modern city was built on top of ancient Hohokam canals — 'rising from the ashes' of a vanished civilization.",
  "Portland": "Portland has more breweries per capita than any city in the world — currently more than 70 within city limits.",
  "San Antonio": "The Alamo was originally a Spanish mission — by the time of the famous 1836 battle, it was already over 100 years old.",
  "San Diego": "San Diego has the most temperate climate of any large US city — average year-round high is 70°F, average low is 57°F.",
  "Savannah": "Savannah is famously haunted — the American Institute of Parapsychology has officially named it the most haunted city in America.",
};

// Returns the funFact for a destination name, or null. Checks the full
// POSTCARDS_CATALOG first (canonical source), then falls back to the
// MISSING_POSTCARD_FACTS map. Used by the post-save fact card.
export function getFunFactForDest(destName) {
  if (!destName) return null;
  const postcard = POSTCARDS_CATALOG.find(p => p.destName === destName);
  if (postcard?.funFact) return postcard.funFact;
  return MISSING_POSTCARD_FACTS[destName] || null;
}

// Returns the postcard ID for a destination name (e.g. "Paris" → "paris"),
// or null if the destination has no entry in POSTCARDS_CATALOG. This ID is
// the key used by PostcardIllustration's switch and by the user's postcard
// collection (jga-postcards). Destinations without a POSTCARDS_CATALOG entry
// won't render a postcard — caller should handle null gracefully (e.g. show
// just the fact card instead).
export function getPostcardIdForDest(destName) {
  if (!destName) return null;
  const postcard = POSTCARDS_CATALOG.find(p => p.destName === destName);
  return postcard?.id || null;
}
